export type AppRuntimeEnv = string

export type AppDomainPreset = {
  /** 项目资源与内置资源 CDN，主要用于 shareres 工程资源读取。 */
  cdn1: string
  /** 素材库 CDN，固定使用正式资源域，不随分支前缀变化。 */
  cdn2: string
  /** 主站域名，用于作品信息、发布同步、用户相关主站接口。 */
  website: string
  /** CG 工具接口域名，用于登录态校验、工程云端、发布、上传初始化等 interapi/pkg 接口。 */
  cg: string
  /** Passport 登录授权域名，用于 OAuth 授权页、头像与账号登录相关接口。 */
  passport: string
}

/**
 * 为指定域名增加分支前缀。
 * @param url 原始完整域名。
 * @param prefix 分支前缀。
 * @returns 增加前缀后的完整域名。
 */
function addDomainPrefix(url: string, prefix: string): string {
  const parsed = new URL(url)
  parsed.hostname = `${prefix}-${parsed.hostname}`
  return parsed.toString().replace(/\/$/g, '')
}

/**
 * 根据正式环境域名生成分支环境域名配置。
 * @param base 正式环境域名配置。
 * @param prefix 分支前缀。
 * @returns 分支环境域名配置。
 */
function createPrefixedDomainPreset(base: AppDomainPreset, prefix: string): AppDomainPreset {
  const cg = addDomainPrefix(base.cg, prefix)
  return {
    ...base,
    website: addDomainPrefix(base.website, prefix),
    cg,
    passport: addDomainPrefix(base.passport, prefix),
  }
}

/**
 * 判断传入值是否符合分支前缀格式。
 * @param value 待校验的环境值。
 * @returns 是否可作为分支前缀。
 */
function isBranchPrefix(value: string): boolean {
  return /^[a-z][a-z0-9-]*$/i.test(value)
}

const PROD_DOMAIN_PRESET: AppDomainPreset = {
  cdn1: 'https://wcdn1.cgyouxi.com',
  cdn2: 'https://wcdn2.cgyouxi.com',
  website: 'https://www.66rpg.com',
  cg: 'https://cg.66rpg.com',
  passport: 'https://passport.66rpg.com',
}

export const APP_DOMAIN_PRESETS: Record<'prod', AppDomainPreset> = {
  prod: PROD_DOMAIN_PRESET,
}

/**
 * 规范化运行环境标识。
 * @param value 外部传入的环境值。
 * @returns 规范化后的运行环境，正式环境统一返回 `prod`，分支环境返回小写前缀。
 */
export function normalizeAppRuntimeEnv(value?: string): AppRuntimeEnv {
  const env = String(value || '').trim()
  if (!env || env === 'prod' || env === 'production' || env === 'development') return 'prod'
  if (isBranchPrefix(env)) return env.toLowerCase()
  return 'prod'
}

/**
 * 获取当前运行环境对应的域名配置。
 * @param value 外部传入的环境值。
 * @returns 正式环境或分支环境域名配置。
 */
export function getAppDomainPreset(value?: string): AppDomainPreset {
  const env = normalizeAppRuntimeEnv(value)
  if (env === 'prod') return APP_DOMAIN_PRESETS.prod
  return createPrefixedDomainPreset(PROD_DOMAIN_PRESET, env)
}
